/**
 * Tenant Query Params Hook
 * Reads `?retailer=X&env=Y` from the URL on mount, applies to the
 * environment store if valid, then strips the params from the URL.
 * @packageDocumentation
 */

import { useEffect } from 'react';

import { useEnvironmentStore } from '@/stores';
import { type Environment, ENVIRONMENT_DISPLAY_NAMES } from '@/types/retailer';

/**
 * Reads optional `?retailer=X&env=Y` query parameters on mount.
 * If both are present and the retailer/environment pair is valid,
 * applies them to the Zustand store and strips the params from the URL.
 */
export const useTenantQueryParams = () => {
  const setCurrentEnvironment = useEnvironmentStore((s) => s.setCurrentEnvironment);
  const retailers = useEnvironmentStore((s) => s.retailers);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const retailer = params.get('retailer');
    const env = params.get('env');

    if (!retailer || !env || !(env in ENVIRONMENT_DISPLAY_NAMES)) {
      return;
    }

    const match = retailers.find((r) => r.retailerCode === retailer);

    if (!match) {
      return;
    }

    const envMatch = match.environments.find((e) => e.environment === env);

    if (!envMatch) {
      return;
    }

    setCurrentEnvironment(retailer, env as Environment);

    // Strip query params from URL without navigation
    params.delete('retailer');
    params.delete('env');
    const remaining = params.toString();
    const newUrl = window.location.pathname + (remaining ? `?${remaining}` : '') + window.location.hash;
    window.history.replaceState(null, '', newUrl);
  }, [retailers, setCurrentEnvironment]);
};
